/**
 * Boot-terminal sign-in gate — missing or invalid auth.json at cold boot.
 *
 * Latches the boot-terminal verdict and clears stale desktop authed flags so
 * AppLayout renders AuthView. Mid-session playback-shell-survives-auth-loss
 * is unchanged: this path no-ops after usable authed state is reached.
 *
 * @see docs/CODE_HEALTH.md
 */

import { shouldKeepPlaybackShellDespiteAuthLoss } from './authDeclaw';
import type { AuthTerminalVerdict } from './authDeclawDeadFamily';
import {
  forceBootTerminalSignInLatch,
  isBootTimeTerminalRejection,
} from './authTerminalLatch';
import { isBootSurrendered } from './bootDeadline';
import { isBootHoldSignInActive, persistBootHoldSignIn } from './authBootHoldSignIn';

const LS_AUTH = 'maestro_auth';
const LS_AUTHED_ONCE = 'maestro_authed_once_v1';
const SS_REFRESH_FAILED = 'maestro_refresh_failed';
const SS_CONNECTOR_AUTH_JSON = 'maestro_connector_has_auth_json';

/** Clear inherited-profile authed flags when boot has no usable auth.json. */
export function purgeStaleAuthedFlagsForMissingAuthJson(): void {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(LS_AUTH);
      localStorage.removeItem(LS_AUTHED_ONCE);
    } catch { /* ignore */ }
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(SS_REFRESH_FAILED);
      sessionStorage.removeItem(SS_CONNECTOR_AUTH_JSON);
    } catch { /* ignore */ }
  }
}

/**
 * Latch boot-terminal sign-in and override stale localStorage authed flags.
 * @param reason terminal verdict already recorded from auth.json restore
 */
export function latchBootTerminalSignInRequired(
  reason: AuthTerminalVerdict = 'reauth_required',
): void {
  forceBootTerminalSignInLatch(reason);
  purgeStaleAuthedFlagsForMissingAuthJson();
  persistBootHoldSignIn('boot_terminal');
}

/** Declaw playback chrome — suppressed when boot-terminal sign-in is pending. */
export function shouldKeepDeclawPlaybackShell(isAuthenticated: boolean): boolean {
  return !isAuthenticated
    && shouldKeepPlaybackShellDespiteAuthLoss()
    && !isBootTimeTerminalRejection();
}

/** True when AppLayout must swap to AuthView instead of the playback shell. */
export function shouldRenderAuthViewInsteadOfPlaybackShell(opts: {
  isAuthenticated: boolean;
  bootSignInRequired: boolean;
  declawPlaybackShell: boolean;
  deadFamilyLogin: boolean;
  authInitTimedOut: boolean;
  sessionBootstrapFailed: boolean;
}): boolean {
  if (opts.isAuthenticated) return false;
  if (isBootHoldSignInActive() || isBootSurrendered()) return true;
  return opts.deadFamilyLogin
    || opts.bootSignInRequired
    || !opts.declawPlaybackShell
    || opts.authInitTimedOut
    || opts.sessionBootstrapFailed;
}

/** 30s tunnel poll must not run while boot-terminal sign-in is pending. */
export function shouldPollLibraryTunnel(isAuthenticated: boolean): boolean {
  return isAuthenticated && !isBootTimeTerminalRejection();
}
